/**
 * Reads the `exp` claim from a JWT access token and returns it as epoch ms.
 *
 * This only *reads* the claim to schedule the session-expiry prompt; it does
 * not verify the signature (the server remains the authority on validity).
 * Returns undefined if the token is missing or malformed.
 */
export const getJwtExpiryMs = (token?: string): number | undefined => {
  if (!token) return undefined;
  try {
    const payload = token.split(".")[1];
    if (!payload) return undefined;
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(
      base64.length + ((4 - (base64.length % 4)) % 4),
      "="
    );
    const { exp } = JSON.parse(atob(padded));
    return typeof exp === "number" ? exp * 1000 : undefined;
  } catch {
    return undefined;
  }
};

/**
 * Absolute expiry time (epoch ms) for an access token. Prefers the token's own
 * `exp` claim, falling back to the API's `expires_in` (seconds) if the claim
 * can't be read.
 */
export const getTokenExpiresAt = (token?: string, expiresIn?: number) =>
  getJwtExpiryMs(token) ?? Date.now() + (expiresIn ?? 0) * 1000;
